import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { StatusBadge } from "@/components/StageBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, Building2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", email: "", spoc: "", city: "", notes: "" };

export default function Owners() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [openO, setOpenO] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/owners", { params: search ? { search } : {} }).then(({ data }) => setRows(data)).finally(() => setLoading(false));
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try { await api.post("/owners", form); setAddOpen(false); setForm(EMPTY); load(); toast.success("Owner added"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Property Owners" subtitle="Owners and every property they hold.">
        <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-56" data-testid="owner-search" />
        <Button size="sm" onClick={() => { setForm(EMPTY); setAddOpen(true); }} data-testid="add-owner-btn"><Plus className="h-4 w-4" /> Add</Button>
      </PageHeader>

      <div className="px-8 py-6">
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0047FF]" /></div>
            : rows.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm" data-testid="owners-empty">No owners yet.</div>
            : (
              <Table>
                <TableHeader><TableRow className="bg-gray-50"><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>City</TableHead><TableHead>SPOC</TableHead><TableHead>Properties</TableHead></TableRow></TableHeader>
                <TableBody>
                  {rows.map((o) => (
                    <TableRow key={o.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setOpenO(o)} data-testid={`owner-row-${o.id}`}>
                      <TableCell className="font-medium">{o.name || "—"}</TableCell>
                      <TableCell>{o.phone || "—"}</TableCell>
                      <TableCell>{o.city || "—"}</TableCell>
                      <TableCell>{o.spoc || "—"}</TableCell>
                      <TableCell>{o.property_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="owner-dialog">
          <DialogHeader><DialogTitle>Add owner</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="o-name" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" data-testid="o-phone" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="o-email" /></div>
            <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1" data-testid="o-city" /></div>
            <div><Label>SPOC</Label><Input value={form.spoc} onChange={(e) => setForm({ ...form, spoc: e.target.value })} className="mt-1" data-testid="o-spoc" /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" data-testid="o-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !form.name} data-testid="o-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OwnerDrawer owner={openO} open={!!openO} onOpenChange={(v) => !v && setOpenO(null)} />
    </div>
  );
}

function OwnerDrawer({ owner, open, onOpenChange }) {
  const [props, setProps] = useState([]);
  useEffect(() => { if (open && owner) api.get(`/owners/${owner.id}`).then(({ data }) => setProps(data.properties)); }, [open, owner]);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[560px] bg-white overflow-y-auto" data-testid="owner-drawer">
        <SheetHeader><SheetTitle className="font-heading">{owner?.name}</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Properties ({props.length})</div>
          {props.length === 0 ? <div className="text-sm text-gray-400 py-6 text-center">No linked properties.</div> :
            props.map((p) => (
              <div key={p.id} className="border border-gray-200 rounded-md p-3 flex items-center justify-between" data-testid={`owner-prop-${p.id}`}>
                <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-gray-400" /><span className="text-sm font-medium">{p.name}</span></div>
                <div className="flex items-center gap-2"><StatusBadge status={p.status} /><span className="text-xs text-gray-400">{p.total_deals} deals</span></div>
              </div>
            ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
